import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import PlayerDemo from './components/PlayerDemo';
import EpisodeList from './components/EpisodeList';
import { dramas } from '@/mocks/dramas';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFavorites } from '@/hooks/useFavorites';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((item) => item.id === id);
  const playerRef = useRef<HTMLDivElement>(null);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [episode, setEpisode] = useState<number | null>(null);

  useEffect(() => {
    setEpisode(drama && drama.currentEpisodes > 0 ? 1 : null);
  }, [drama]);

  const related = useMemo(() => {
    if (!drama) return [];
    const sameType = dramas
      .filter((item) => item.id !== drama.id && item.types.some((t) => drama.types.includes(t)))
      .sort((a, b) => b.hot - a.hot);
    if (sameType.length >= 4) return sameType.slice(0, 4);
    const fallback = dramas
      .filter((item) => item.id !== drama.id && !sameType.includes(item))
      .sort((a, b) => b.hot - a.hot);
    return [...sameType, ...fallback].slice(0, 4);
  }, [drama]);

  useDocumentMeta({
    title: drama
      ? `${drama.title}免费在线观看 - 高清播放 - 香草在线观看免费播放电视剧`
      : '剧集暂时不可用 - 香草在线观看免费播放电视剧',
    description: drama
      ? `${drama.title}免费在线观看，${drama.year}年${drama.region}${drama.types.join('、')}电视剧，由${drama.director}执导，${drama.actors.join('、')}主演，${drama.status}，共${drama.totalEpisodes}集。`
      : '该剧集暂时不可用，请稍后重试或浏览其他高清电视剧。',
    keywords: drama ? `${drama.title},${drama.title}在线观看,${drama.types.join(',')}电视剧,香草在线观看` : '香草在线观看,电视剧在线观看',
  });

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 md:pt-20 flex items-center justify-center px-4">
          <div className="text-center py-20">
            <span className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
              <i className="ri-emotion-sad-line text-4xl" />
            </span>
            <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">剧集暂时不可用，请稍后重试</h1>
            <p className="mt-3 text-sm text-foreground-500">没有找到你访问的剧集信息，它可能已被移除或链接有误。</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-apps-2-line" />
                返回分类页
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100 border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-4-line" />
                回到首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const favorited = isFavorite(drama.id);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={drama.backdrop}
              alt={`${drama.title}背景图`}
              title={`${drama.title} 高清电视剧在线观看`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/35" />
          </div>

          <div className="relative w-full px-4 md:px-8 py-8 md:py-12 text-background-50">
            <nav className="text-xs md:text-sm text-background-200/85 flex items-center gap-1.5 flex-wrap mb-6" aria-label="面包屑">
              <Link to="/" className="hover:text-accent-300">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/category" className="hover:text-accent-300">电视剧分类</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={`/category/${encodeURIComponent(drama.types[0])}`} className="hover:text-accent-300">{drama.types[0]}</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-50">{drama.title}</span>
            </nav>

            <div className="flex flex-col sm:flex-row gap-6 md:gap-8">
              <div className="w-36 md:w-52 shrink-0 self-center sm:self-start">
                <div className="rounded-lg overflow-hidden border border-background-50/20 bg-background-950/30">
                  <img
                    src={drama.cover}
                    alt={`${drama.title}封面海报`}
                    title={`${drama.title} - 香草视频`}
                    className="w-full aspect-[2/3] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading font-extrabold text-2xl md:text-4xl">{drama.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs md:text-sm text-background-100/90">
                  <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 font-medium">{drama.status}</span>
                  <span>{drama.year}</span>
                  <span className="text-background-100/40">·</span>
                  <span>{drama.region}</span>
                  <span className="text-background-100/40">·</span>
                  <span>共 {drama.totalEpisodes} 集</span>
                  <span className="text-background-100/40">·</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-accent-300" />
                    热度 {drama.hot}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {drama.types.map((type) => (
                    <Link
                      key={type}
                      to={`/category/${encodeURIComponent(type)}`}
                      className="px-2.5 py-1 rounded-md bg-background-50/15 backdrop-blur-sm text-xs hover:bg-background-50/25 transition-colors"
                    >
                      {type}
                    </Link>
                  ))}
                </div>

                <p className="mt-4 text-sm md:text-base text-background-100/90 max-w-2xl">
                  {drama.actors.join(' / ')} 领衔主演
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg" />
                    立即观看
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(drama.id)}
                    className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      favorited
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50/15 backdrop-blur-sm text-background-50 hover:bg-background-50/25'
                    }`}
                  >
                    <i className={favorited ? 'ri-heart-3-fill text-lg' : 'ri-heart-3-line text-lg'} />
                    {favorited ? '已收藏' : '收藏'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-8 md:py-12 flex flex-col gap-10">
          <div ref={playerRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <PlayerDemo title={drama.title} poster={drama.backdrop} episode={episode} />
            </div>
            <div className="lg:col-span-1 rounded-lg bg-background-100 border border-background-200 p-5">
              <EpisodeList
                total={drama.totalEpisodes}
                available={drama.currentEpisodes}
                current={episode}
                onSelect={(ep) => setEpisode(ep)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-4">剧情简介</h2>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">{drama.synopsis}</p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                《{drama.title}》是一部 {drama.year} 年播出的{drama.region}{drama.types.join('、')}题材电视剧，
                由 {drama.director} 执导，{drama.actors.join('、')}主演，{drama.status === '已完结' ? `全 ${drama.totalEpisodes} 集已完结` : `目前更新至第 ${drama.currentEpisodes} 集`}。
                本页面内容均为演示示例，播放功能仅作界面展示。
              </p>
            </div>

            <div className="lg:col-span-1">
              <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-4">基本资料</h2>
              <dl className="rounded-lg bg-background-100 border border-background-200 divide-y divide-background-200">
                <InfoRow label="类型" value={drama.types.join(' / ')} />
                <InfoRow label="主演" value={drama.actors.join('、')} />
                <InfoRow label="导演" value={drama.director} />
                <InfoRow label="地区" value={drama.region} />
                <InfoRow label="年份" value={`${drama.year}`} />
                <InfoRow label="首播时间" value={drama.premiereDate} />
                <InfoRow label="更新状态" value={drama.status === '已完结' ? `全 ${drama.totalEpisodes} 集完结` : `更新至第 ${drama.currentEpisodes} 集`} />
                <InfoRow label="更新时间" value={drama.updatedAt} />
              </dl>
            </div>
          </div>

          <section>
            <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-950 mb-5">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((item, index) => (
                <DramaCard key={item.id} drama={item} index={index} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3 text-sm">
      <dt className="w-20 shrink-0 text-foreground-500">{label}</dt>
      <dd className="flex-1 text-foreground-900">{value}</dd>
    </div>
  );
}